import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { NextApiRequest, NextApiResponse } from 'next';
import { setupCache } from 'axios-cache-interceptor';
import { authService } from 'ui/services';
import { sendErrorResponse } from '../lib/response';
import { clearAuthCookies } from '../lib/cookies';
import { extractFromCookies } from '../helpers/utils';
import { serialize } from 'cookie';
import { captureException } from '@sentry/nextjs';
import { httpKeepAliveAgent, httpsKeepAliveAgent } from '../helpers/utils/connectionPoolConfig';

/*
 * For server side only usage (getServerSideProps, API routes)
 */
export class APIProxy {
  private axiosInstance: AxiosInstance;
  private req: NextApiRequest;
  private res: NextApiResponse;
  private isAuth: boolean;
  private systemToken: string;
  private refreshToken: string;
  private retry: number;
  private isUsingSystemToken: boolean;

  constructor(req: NextApiRequest, res: NextApiResponse) {
    this.req = req;
    this.res = res;
    this.retry = 0;
    const refreshToken = extractFromCookies('refresh-token', this.req?.headers?.cookie);
    const systemToken = extractFromCookies('system_token', this.req?.headers?.cookie);
    this.systemToken = systemToken;
    this.refreshToken = refreshToken;
    this.axiosInstance = setupCache(
      axios.create({
        httpAgent: httpKeepAliveAgent,
        httpsAgent: httpsKeepAliveAgent,
        baseURL: process.env.NEXT_PUBLIC_BASE_URL,
        validateStatus: (status) => status < 500,
      }),
      {
        ttl: 500,
      }
    );
    this.setupInterceptors();

    if (
      process.env.NEXT_PUBLIC_CONNECTION_POOL_STATUS === 'true' &&
      typeof window === 'undefined'
    ) {
      setInterval(() => {
        const stats = {
          http: this.axiosInstance?.defaults?.httpAgent?.getCurrentStatus(),
          https: this.axiosInstance?.defaults?.httpsAgent?.getCurrentStatus(),
        };
        console.log('Connection Pool Stats:', stats);
      }, 30000); // Log every 30 seconds
    }
  }

  setupInterceptors() {
    // Request interceptor
    this.axiosInstance.interceptors.request.use(
      async (config) => {
        if (!config.headers.Authorization) {
          this.isAuth = false;
          this.isUsingSystemToken = true;
          config.headers.Authorization = `Bearer ${this.systemToken}`;
        } else {
          this.isAuth = true;
        }
        return config;
      },
      (error) => {
        // Capture error to sentry
        captureException(error);
        return Promise.reject(error);
      }
    );
    // Response interceptor
    this.axiosInstance.interceptors.response.use(
      async (response) => {
        const originalRequest = response.config;
        if (response.status === 403 || response.status === 401) {
          // Prevent infinite retry loops
          if (this.retry > 3) {
            return Promise.reject(response); // Already retried, so reject to prevent loop
          }
          this.retry += 1;

          if (!this.isAuth || this.isUsingSystemToken) {
            this.isAuth = true;

            // Clear existing system token cookie
            this.res.setHeader('Set-Cookie', [
              serialize('system_token', '', {
                maxAge: -1,
                path: '/',
                httpOnly: true,
              }),
            ]);

            try {
              const authResponse = await authService.getSystemAuth(true);
              if (authResponse.status !== 200) {
                sendErrorResponse({
                  res: this.res,
                  message: 'invalid system token',
                  status: 403,
                  type: 'InvalidSystemToken',
                  shouldRedirect: false,
                });
                return Promise.reject(response); // Reject original response if system auth fails
              }
              // Set new system token cookie
              this.res.setHeader('Set-Cookie', [
                serialize('system_token', authResponse.data?.token, {
                  path: '/',
                  httpOnly: true,
                }),
              ]);
              // Update Authorization header for the original request and retry
              originalRequest.headers['Authorization'] = `Bearer ${authResponse.data?.token}`;
              return this.axiosInstance(originalRequest);
            } catch (error) {
              // Capture error to sentry
              captureException(error);
              return Promise.reject(error);
            }
          } else {
            this.res.setHeader('Set-Cookie', [
              serialize('system_token', '', {
                maxAge: -1,
                path: '/',
                httpOnly: true,
              }),
            ]);

            try {
              const refreshResponse = await authService.authRefreshToken(this.refreshToken);
              const authResponse = await authService.getSystemAuth(true);
              if (refreshResponse.status !== 200) {
                sendErrorResponse({
                  res: this.res,
                  message: 'invalid refresh token',
                  status: 401,
                  type: 'InvalidRefreshToken',
                  shouldRedirect: true,
                });
                return Promise.reject(response); // Reject original response if refresh token fails
              }
              // Clear old auth cookies and set new access token
              clearAuthCookies(this.res);
              this.res.setHeader('Set-Cookie', [
                serialize('access-token', refreshResponse.data?.accessToken, {
                  path: '/',
                  httpOnly: true,
                }),
                serialize('refresh-token', refreshResponse.data?.refreshToken, {
                  path: '/',
                  httpOnly: true,
                }),
                serialize('system_token', authResponse.data?.token, {
                  path: '/',
                  httpOnly: true,
                }),
              ]);
              // Update Authorization header for the original request and retry
              originalRequest.headers['Authorization'] =
                `Bearer ${refreshResponse.data?.accessToken}`;
              return this.axiosInstance(originalRequest);
            } catch (error) {
              // Capture error to sentry
              captureException(error);
              return Promise.reject(error);
            }
          }
        }

        if (response.status >= 400 && response.status <= 599) {
          return Promise.reject({
            ...response,
            response: {
              data: response.data,
            },
          });
        } else {
          return response;
        }
      },
      (error) => {
        // Capture error to sentry
        captureException(error);
        return Promise.reject(error);
      }
    );
  }

  /**
   * Makes an HTTP request using the configured Axios instance.
   * @param config The Axios request configuration.
   * @returns A Promise that resolves to the AxiosResponse.
   */
  async makeRequest<T = any, R = any>(
    config: AxiosRequestConfig<R>
  ): Promise<import('axios').AxiosResponse<T, R>> {
    return this.axiosInstance.request<T, import('axios').AxiosResponse<T, R>>(config);
  }
}
